/**
 * Per-user preference defaults, types, and sanitization for the
 * /api/user/notification-preferences endpoints, the per-user toast gate, and
 * (SPEC-1 senior-prefs) the Senior/Admin behaviour switches + saved filters.
 *
 * SPEC-qam-backlog Session 11 — single source of truth for the group→pref key
 * mapping so use-notification-toasts.ts and the settings UI never drift.
 *
 * SPEC-1 senior-prefs (2026-09-23) — the row grows four typed columns
 * (auto_advance, show_expected_actual, show_queue_footer, saved_filters). The
 * old "Decision & Auto-Advance" toast switch is retired from the UI (D3): its
 * column stays, is no longer read, and the `autoAdvance` toast group now maps
 * to "always show".
 */

export interface NotificationPrefs {
  /** Retired from the UI (SPEC-1 D3). Column kept, never read. */
  showAutoAdvance: boolean;
  showActionConfirmations: boolean;
  showOtherActivity: boolean;
  showClaimAlerts: boolean;
}

export const DEFAULT_NOTIFICATION_PREFS: NotificationPrefs = {
  showAutoAdvance: true,
  showActionConfirmations: true,
  showOtherActivity: true,
  showClaimAlerts: true,
};

/** The four call-site notification groups used by showToast(). */
export type NotificationGroup =
  | "autoAdvance"
  | "actionConfirmation"
  | "otherActivity"
  | "claimAlert";

/**
 * Maps each NotificationGroup to the corresponding NotificationPrefs key.
 * `null` = always shown (no switch). The "Loading next delivery…" toast shows
 * whenever the page actually advances (SPEC-1 D3).
 */
export const GROUP_TO_PREF_KEY: Record<NotificationGroup, keyof NotificationPrefs | null> = {
  autoAdvance: null,
  actionConfirmation: "showActionConfirmations",
  otherActivity: "showOtherActivity",
  claimAlert: "showClaimAlerts",
};

/** Metadata for the notification settings UI — one entry per toggle. */
export interface NotificationGroupMeta {
  group: NotificationGroup;
  prefKey: keyof NotificationPrefs;
  label: string;
  description: string;
}

export const NOTIFICATION_GROUP_META: NotificationGroupMeta[] = [
  {
    group: "actionConfirmation",
    prefKey: "showActionConfirmations",
    label: "Edit Confirmations",
    description:
      "Show a toast when saving changes to Expected/Actual, drop zone, recipient, flags, hidden reference photos, or Podium messages.",
  },
  {
    group: "otherActivity",
    prefKey: "showOtherActivity",
    label: "Other Activity",
    description:
      "Show a toast when a new delivery arrives in queue.",
  },
  {
    group: "claimAlert",
    prefKey: "showClaimAlerts",
    label: "Claim Alerts",
    description:
      "Show a toast when a delivery you have claimed is taken over by another reviewer or when your claim is about to expire.",
  },
];

const NOTIFICATION_PREF_KEYS: (keyof NotificationPrefs)[] = [
  "showAutoAdvance",
  "showActionConfirmations",
  "showOtherActivity",
  "showClaimAlerts",
];

/**
 * Legacy sanitizer (Session 11): strips unknown keys and non-boolean values.
 * Kept for callers that only deal in the four notification switches; the
 * API itself uses sanitizeUserPrefsPatch below.
 */
export function sanitizeNotificationPrefsPatch(
  body: unknown,
): Partial<NotificationPrefs> {
  if (!body || typeof body !== "object" || Array.isArray(body)) return {};
  const obj = body as Record<string, unknown>;
  const patch: Partial<NotificationPrefs> = {};
  for (const key of NOTIFICATION_PREF_KEYS) {
    if (typeof obj[key] === "boolean") {
      patch[key] = obj[key] as boolean;
    }
  }
  return patch;
}

// ─── SPEC-1 senior-prefs ─────────────────────────────────────────────────────

export const SAVED_FILTER_SORTS = ["completed_desc", "completed_asc", "qa_completed_desc", "qa_completed_asc"] as const;
export type SavedFilterSort = (typeof SAVED_FILTER_SORTS)[number];

/** A personal Senior list filter: the five dropdown sets + the sort. Never date, tab, q or attention. */
export interface SavedFilter {
  /** uuid, server-assigned on create */
  id: string;
  /** 1–60 chars, trimmed, unique per user (case-insensitive) */
  name: string;
  sort: SavedFilterSort;
  agentStatus: string[];
  owner: string[];
  driver: string[];
  match: string[];
  bag: string[];
}

export const MAX_SAVED_FILTERS = 20;
export const SAVED_FILTER_NAME_MAX = 60;
export const SAVED_FILTER_ARRAY_KEYS = ["agentStatus", "owner", "driver", "match", "bag"] as const;
/** List-view state that must never be frozen into a saved filter. */
export const SAVED_FILTER_FORBIDDEN_KEYS = ["date", "tab", "q", "attention"] as const;
const SAVED_FILTER_ALLOWED_KEYS = new Set<string>(["id", "name", "sort", ...SAVED_FILTER_ARRAY_KEYS]);

export interface UserPrefs extends NotificationPrefs {
  /** Senior/Admin. Default on. Off = the page stays put after Agree / Good / Skip and the server does not pick-and-claim. */
  autoAdvance: boolean;
  /** Senior/Admin. Default off. Wired on the detail page in Phase 3. */
  showExpectedActual: boolean;
  /** Senior/Admin. Default off. Wired on the detail page in Phase 3. */
  showQueueFooter: boolean;
  /** Senior/Admin. Personal, max 20. */
  savedFilters: SavedFilter[];
}

export const SENIOR_PREF_KEYS = ["autoAdvance", "showExpectedActual", "showQueueFooter", "savedFilters"] as const;
export type SeniorPrefKey = (typeof SENIOR_PREF_KEYS)[number];

export const DEFAULT_USER_PREFS: UserPrefs = {
  ...DEFAULT_NOTIFICATION_PREFS,
  autoAdvance: true,
  showExpectedActual: false,
  showQueueFooter: false,
  savedFilters: [],
};

const BOOLEAN_PREF_KEYS: (keyof UserPrefs)[] = [
  ...NOTIFICATION_PREF_KEYS,
  "autoAdvance",
  "showExpectedActual",
  "showQueueFooter",
];

export type SanitizeResult =
  | { ok: true; patch: Partial<UserPrefs> }
  | { ok: false; error: string };

function sanitizeSavedFilters(raw: unknown): { ok: true; filters: SavedFilter[] } | { ok: false; error: string } {
  if (!Array.isArray(raw)) return { ok: false, error: "savedFilters must be an array" };
  if (raw.length > MAX_SAVED_FILTERS) return { ok: false, error: `savedFilters: at most ${MAX_SAVED_FILTERS} filters` };
  const seen = new Set<string>();
  const filters: SavedFilter[] = [];
  for (let i = 0; i < raw.length; i++) {
    const entry: unknown = raw[i];
    if (!entry || typeof entry !== "object" || Array.isArray(entry)) {
      return { ok: false, error: `savedFilters[${i}] must be an object` };
    }
    const f = entry as Record<string, unknown>;
    for (const key of Object.keys(f)) {
      if ((SAVED_FILTER_FORBIDDEN_KEYS as readonly string[]).includes(key)) {
        return { ok: false, error: `savedFilters[${i}]: '${key}' cannot be saved in a filter` };
      }
      if (!SAVED_FILTER_ALLOWED_KEYS.has(key)) {
        return { ok: false, error: `savedFilters[${i}]: unknown key '${key}'` };
      }
    }
    if (f.id !== undefined && (typeof f.id !== "string" || f.id.length === 0)) {
      return { ok: false, error: `savedFilters[${i}]: id must be a non-empty string when present` };
    }
    if (typeof f.name !== "string") return { ok: false, error: `savedFilters[${i}]: name must be a string` };
    const name = f.name.trim();
    if (name.length < 1 || name.length > SAVED_FILTER_NAME_MAX) {
      return { ok: false, error: `savedFilters[${i}]: name must be 1–${SAVED_FILTER_NAME_MAX} characters` };
    }
    const lower = name.toLowerCase();
    if (seen.has(lower)) return { ok: false, error: `savedFilters: duplicate name '${name}'` };
    seen.add(lower);
    if (typeof f.sort !== "string" || !(SAVED_FILTER_SORTS as readonly string[]).includes(f.sort)) {
      return { ok: false, error: `savedFilters[${i}]: sort must be one of ${SAVED_FILTER_SORTS.join(", ")}` };
    }
    const arrays: Record<(typeof SAVED_FILTER_ARRAY_KEYS)[number], string[]> = {
      agentStatus: [], owner: [], driver: [], match: [], bag: [],
    };
    for (const key of SAVED_FILTER_ARRAY_KEYS) {
      const v = f[key];
      if (v === undefined) continue;
      if (!Array.isArray(v) || v.some((x) => typeof x !== "string")) {
        return { ok: false, error: `savedFilters[${i}]: ${key} must be an array of strings` };
      }
      arrays[key] = [...(v as string[])];
    }
    const out: SavedFilter = {
      id: f.id as string,
      name,
      sort: f.sort as SavedFilterSort,
      ...arrays,
    };
    if (f.id === undefined) delete (out as Partial<SavedFilter>).id;
    filters.push(out);
  }
  return { ok: true, filters };
}

/**
 * Validates a PATCH body for /api/user/notification-preferences. Unlike the
 * legacy sanitizer this REJECTS instead of stripping: an unknown key, a
 * non-boolean where a boolean is expected, or an invalid savedFilters list
 * fails the whole request (caller answers 400 with `error`).
 *
 * Saved-filter `id`s are passed through when present and left undefined when
 * missing; the server assigns them (`assignSavedFilterIds`).
 */
export function sanitizeUserPrefsPatch(body: unknown): SanitizeResult {
  if (!body || typeof body !== "object" || Array.isArray(body)) {
    return { ok: false, error: "Body must be an object" };
  }
  const obj = body as Record<string, unknown>;
  const patch: Partial<UserPrefs> = {};
  for (const [key, value] of Object.entries(obj)) {
    if (key === "savedFilters") {
      const r = sanitizeSavedFilters(value);
      if (!r.ok) return r;
      patch.savedFilters = r.filters;
      continue;
    }
    if ((BOOLEAN_PREF_KEYS as string[]).includes(key)) {
      if (typeof value !== "boolean") return { ok: false, error: `${key} must be a boolean` };
      (patch as Record<string, unknown>)[key] = value;
      continue;
    }
    return { ok: false, error: `Unknown preference '${key}'` };
  }
  if (Object.keys(patch).length === 0) return { ok: false, error: "No preference fields provided" };
  return { ok: true, patch };
}

/** True when the patch carries any Senior/Admin-only field (role gate in the route). */
export function patchTouchesSeniorPrefs(patch: Partial<UserPrefs>): boolean {
  return SENIOR_PREF_KEYS.some((k) => k in patch);
}

/** Server helper: give every filter without an id a fresh uuid. Pure given `uuid`. */
export function assignSavedFilterIds(filters: SavedFilter[], uuid: () => string): SavedFilter[] {
  return filters.map((f) => (f.id ? f : { ...f, id: uuid() }));
}
